import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Loader2, ChevronLeft, ChevronRight, Clipboard, Calendar, Clock, User, Eye, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { auditService } from '@/services/audit.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';

const actionLabels: Record<string, { label: string; color: string }> = {
  LOGIN: { label: 'Inicio de Sesión', color: 'bg-ios-green/10 text-ios-green border border-ios-green/20' },
  OPEN_REGISTER: { label: 'Apertura de Caja', color: 'bg-blue-50 text-ios-blue border border-ios-blue/20' },
  CLOSE_REGISTER: { label: 'Cierre de Caja (Corte Z)', color: 'bg-purple-50 text-purple-600 border border-purple-200' },
  CANCEL_SALE: { label: 'Cancelación de Venta', color: 'bg-red-50 text-ios-red border border-ios-red/20' },
  PRICE_CHANGE: { label: 'Cambio de Precio', color: 'bg-cyan-50 text-cyan-700 border border-cyan-200' },
  STOCK_IN: { label: 'Entrada de Inventario', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  STOCK_OUT: { label: 'Salida de Inventario', color: 'bg-orange-50 text-orange-700 border border-orange-200' },
};

const entityLabels: Record<string, string> = {
  USER: 'Usuario',
  CASH_REGISTER: 'Caja Registradora',
  SALE: 'Venta',
  PRODUCT: 'Producto',
};

const keyTranslations: Record<string, string> = {
  saleId: 'ID de la Venta',
  folio: 'Folio',
  grandTotal: 'Total de la Venta',
  reason: 'Motivo / Razón',
  customerName: 'Cliente',
  productId: 'ID del Producto',
  sku: 'SKU / Código',
  name: 'Producto',
  changes: 'Cambios de Precio',
  quantity: 'Cantidad',
  stockBefore: 'Stock Anterior',
  stockAfter: 'Stock Nuevo',
  registerId: 'ID de Caja',
  initialCash: 'Efectivo Inicial',
  note: 'Observaciones / Notas',
  countedCash: 'Efectivo Contado',
  expectedCash: 'Efectivo Esperado',
  difference: 'Diferencia (Corte Z)',
  username: 'Usuario',
  fullName: 'Nombre Completo',
  pricePublic: 'Precio Público',
  priceWholesale: 'Precio Mayoreo',
  priceDistributor: 'Precio Distribuidor',
  priceVip: 'Precio VIP',
};

const formatValue = (key: string, value: any): string => {
  if (value === null || value === undefined) return '—';
  
  // Format currency fields
  const currencyFields = ['grandTotal', 'initialCash', 'countedCash', 'expectedCash', 'difference', 'old', 'new', 'pricePublic', 'priceWholesale', 'priceDistributor', 'priceVip'];
  if (currencyFields.includes(key) && !isNaN(Number(value))) {
    return `$${Number(value).toFixed(2)}`;
  }
  
  return String(value);
};

export function AuditPage() {
  const { user } = useAuthStore();
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<any | null>(null);

  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const limit = 20;

  const loadLogs = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await auditService.findAll({
        companyId: user.company.id,
        action: actionFilter || undefined,
        page,
        limit,
      });
      setLogs(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [user, actionFilter, page]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const parseDetails = (detailsStr: string) => {
    try {
      return JSON.parse(detailsStr);
    } catch {
      return detailsStr;
    }
  };

  const renderModalDetails = (details: any) => {
    if (typeof details !== 'object' || details === null) {
      return <p className="text-sm text-ios-gray">{String(details)}</p>;
    }

    return (
      <div className="space-y-3">
        {Object.entries(details).map(([key, value]) => {
          // Si el valor es el objeto de cambios de precios (changes)
          if (key === 'changes' && typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="bg-ios-gray-6 rounded-xl p-3.5 border border-ios-gray-5">
                <span className="font-bold text-ios-gray uppercase text-[10px] tracking-wider block mb-2">
                  {keyTranslations[key] || key}
                </span>
                <div className="space-y-2">
                  {Object.entries(value).map(([subKey, subVal]: any) => (
                    <div key={subKey} className="flex justify-between items-center text-sm border-b border-ios-gray-5/50 pb-1.5 last:border-0 last:pb-0">
                      <span className="font-semibold text-ios-gray">
                        {keyTranslations[subKey] || subKey}
                      </span>
                      <span className="text-foreground font-medium">
                        {subVal.old !== undefined 
                          ? `${formatValue(subKey, subVal.old)} → ${formatValue(subKey, subVal.new)}` 
                          : JSON.stringify(subVal)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Si el valor es cualquier otro objeto
          if (typeof value === 'object' && value !== null) {
            return (
              <div key={key} className="bg-ios-gray-6 rounded-xl p-3.5 border border-ios-gray-5">
                <span className="font-bold text-ios-gray uppercase text-[10px] tracking-wider block mb-2">
                  {keyTranslations[key] || key}
                </span>
                <div className="space-y-1.5 pl-2">
                  {Object.entries(value).map(([subKey, subVal]: any) => (
                    <div key={subKey} className="flex justify-between items-center text-sm border-b border-ios-gray-5/30 pb-1 last:border-0 last:pb-0">
                      <span className="font-semibold text-ios-gray">{keyTranslations[subKey] || subKey}</span>
                      <span className="text-foreground font-medium">{formatValue(subKey, subVal)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={key} className="flex justify-between items-center text-sm border-b border-ios-gray-5 pb-2 last:border-0 last:pb-0">
              <span className="font-semibold text-ios-gray">
                {keyTranslations[key] || key}
              </span>
              <span className="text-foreground font-medium text-right">
                {formatValue(key, value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadLogs} onLogin={handleAuthRedirect} />
      
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Bitácora de Auditoría</h1>
              <p className="text-sm text-ios-gray mt-1">{total} actividades registradas</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm max-w-xs"
            >
              <option value="">Todas las acciones</option>
              {Object.entries(actionLabels).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Logs table */}
          <div className="ios-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                    <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Fecha / Hora</th>
                    <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Usuario</th>
                    <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Sucursal</th>
                    <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acción</th>
                    <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Entidad</th>
                    <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Detalles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ios-gray-5">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <Loader2 size={24} className="animate-spin mx-auto text-ios-gray" />
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-ios-gray text-sm">
                        No se encontraron registros de auditoría
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const actionInfo = actionLabels[log.action] || { label: log.action, color: 'bg-ios-gray-6 text-ios-gray' };
                      
                      return (
                        <tr key={log.id} className="hover:bg-ios-gray-6/20 transition-colors">
                          <td className="px-4 py-3 text-xs">
                            <div className="flex items-center gap-1.5 font-medium">
                              <Calendar size={13} className="text-ios-gray" />
                              {new Date(log.createdAt).toLocaleDateString('es-MX')}
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-ios-gray mt-0.5">
                              <Clock size={11} />
                              {new Date(log.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-ios-gray-6 rounded-full flex items-center justify-center text-xs text-ios-gray font-semibold">
                                {log.user.fullName.charAt(0)}
                              </div>
                              <div>
                                <p className="text-xs font-semibold">{log.user.fullName}</p>
                                <p className="text-[10px] text-ios-gray">@{log.user.username}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs font-medium text-ios-gray">
                            {log.branch?.name || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${actionInfo.color}`}>
                              {actionInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <span className="font-semibold text-ios-gray">
                              {entityLabels[log.entity] || log.entity}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-ios-gray-6 hover:bg-ios-blue/10 hover:text-ios-blue text-xs text-ios-gray font-medium transition-colors"
                            >
                              <Eye size={14} />
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-ios-gray-5">
                <span className="text-xs text-ios-gray">Página {page} de {totalPages}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Floating Detail Modal (iOS-style premium) */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[20px] w-full max-w-lg max-h-[90vh] flex flex-col shadow-ios-xl animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <div>
                <h3 className="font-semibold text-base">Detalle de Operación</h3>
                <p className="text-xs text-ios-gray">
                  {new Date(selectedLog.createdAt).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'medium' })}
                </p>
              </div>
              <button 
                onClick={() => setSelectedLog(null)} 
                className="p-2 text-ios-gray hover:bg-ios-gray-6 rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Operator Info */}
              <div className="bg-ios-gray-6 rounded-xl p-3.5 flex items-center gap-3 border border-ios-gray-5">
                <div className="w-9 h-9 bg-ios-blue/10 rounded-full flex items-center justify-center text-ios-blue text-sm font-semibold">
                  {selectedLog.user.fullName.charAt(0)}
                </div>
                <div>
                  <p className="text-xs text-ios-gray uppercase tracking-wider text-[9px] font-bold">Realizado Por</p>
                  <p className="text-sm font-semibold text-foreground">{selectedLog.user.fullName}</p>
                  <p className="text-xs text-ios-gray">Usuario: @{selectedLog.user.username}</p>
                </div>
              </div>

              {/* Action Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-ios-gray-6 rounded-xl p-3 border border-ios-gray-5">
                  <p className="text-[9px] text-ios-gray font-bold uppercase tracking-wider">Acción</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold mt-1 ${actionLabels[selectedLog.action]?.color || ''}`}>
                    {actionLabels[selectedLog.action]?.label || selectedLog.action}
                  </span>
                </div>
                <div className="bg-ios-gray-6 rounded-xl p-3 border border-ios-gray-5">
                  <p className="text-[9px] text-ios-gray font-bold uppercase tracking-wider">Módulo / Entidad</p>
                  <p className="text-sm font-semibold mt-1 text-foreground">
                    {entityLabels[selectedLog.entity] || selectedLog.entity}
                  </p>
                </div>
              </div>

              {/* Technical Details (Translated & Formatted) */}
              <div>
                <h4 className="text-xs font-bold text-ios-gray uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                  <Clipboard size={14} className="text-ios-blue" />
                  Detalles Técnicos
                </h4>
                <div className="border border-ios-gray-5 rounded-xl p-3.5 bg-white shadow-sm">
                  {renderModalDetails(parseDetails(selectedLog.details))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-ios-gray-5 bg-ios-gray-6/30">
              <button
                onClick={() => setSelectedLog(null)}
                className="w-full py-2.5 bg-ios-blue hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm shadow-blue-500/20"
              >
                Cerrar Detalle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
